import { API_BASE_URL } from '../config/constants';
import { logger } from '../utils/logger';

export interface HealthStatus {
  isHealthy: boolean;
  lastChecked: Date;
  error?: string;
}

class HealthService {
  private healthStatus: HealthStatus = {
    isHealthy: true,
    lastChecked: new Date(),
  };

  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private subscribers: Array<(status: HealthStatus) => void> = [];

  /**
   * Check the health of the API
   */
  async checkHealth(): Promise<HealthStatus> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = (await response.json()) as { status: string };
        this.healthStatus = {
          isHealthy: data.status === 'healthy',
          lastChecked: new Date(),
          error: undefined,
        };
      } else {
        this.healthStatus = {
          isHealthy: false,
          lastChecked: new Date(),
          error: `API returned status ${response.status}`,
        };
      }
    } catch (error) {
      logger.error('Health check failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.healthStatus = {
        isHealthy: false,
        lastChecked: new Date(),
        error: errorMessage.includes('aborted') ? 'Request timeout' : errorMessage,
      };
    }

    // Notify subscribers
    this.notifySubscribers();

    return this.healthStatus;
  }

  /**
   * Start periodic health checks
   */
  startPeriodicChecks(intervalMs: number = 30000): void {
    // Clear any existing interval
    this.stopPeriodicChecks();

    // Initial check
    this.checkHealth();

    // Set up periodic checks
    this.checkInterval = setInterval(() => {
      this.checkHealth();
    }, intervalMs);
  }

  /**
   * Stop periodic health checks
   */
  stopPeriodicChecks(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Subscribe to health status changes
   */
  subscribe(callback: (status: HealthStatus) => void): () => void {
    this.subscribers.push(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  /**
   * Get current health status
   */
  getStatus(): HealthStatus {
    return this.healthStatus;
  }

  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => {
      try {
        callback(this.healthStatus);
      } catch (error) {
        logger.error('Error in health status subscriber:', error);
      }
    });
  }
}

// Export singleton instance
export const healthService = new HealthService();
